import * as React from 'react'
import { t } from '@transifex/native'

import {
  CheckOutlined,
  CopyOutlined,
  EditOutlined,
} from 'src/icons/antd-compat'
import { cn } from 'src/lib/utils'
import { variants } from 'src/styles/type-scale'

import type {
  LinkProps as AntdLinkProps,
  ParagraphProps as AntdParagraphProps,
  TextProps as AntdTextProps,
  TitleProps as AntdTitleProps,
} from './antd-props'
import { Tooltip } from './Tooltip'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Typography` seam. `./Typography.tsx` is the
 * dispatcher that chooses between this and antd's own Typography; nothing else
 * imports this file, and nothing reaches it until someone flips the design
 * toggle (`src/design-mode`).
 *
 * Ported from the `update-design` reference branch's `src/antd/Typography.tsx`
 * (its Phase 2 slice 11 part D). Divergences from the reference are marked
 * `[development]` below; everything else is the reference verbatim.
 *
 * Two different "Typography" components exist in this repo and they must not
 * be confused:
 *   1. `src/storybook/components/Typography` — the app's OWN, with
 *      `variant`/`weight`/`loading` props, already wired to the type scale in
 *      `src/styles/type-scale.ts`. It renders antd's Typography underneath, so
 *      it moves onto this seam with no edit of its own.
 *   2. antd's `Typography` (`.Text` / `.Title` / `.Paragraph` / `.Link`) — 478
 *      files, all of them through the `src/antd` barrel. This seam replaces it.
 *
 * The EXTERNAL API is antd's own prop types, so every one of those call sites
 * switches with no edit at all. Substituting looser types here would widen
 * callback parameters to `any` at call sites and break `noImplicitAny` in
 * files this change never touched.
 *
 * The type scale is the single source of size/line-height, so `Title`'s levels
 * map onto it rather than carrying their own font sizes: 1→header-32,
 * 2→header-24, 3→subHeader-20, 4→subHeader-16, 5→body-14.
 * `Text`/`Paragraph`/`Link` inherit their size rather than forcing `body-14`,
 * because the storybook wrapper sets the variant itself and an inherited size
 * is what lets a Text inside a heading or a table cell match its surroundings.
 *
 * `.ant-typography*` names are re-emitted, and they are REQUIRED, not merely
 * safe: `api/consts-client/TicketTypeLevelType.tsx` styles `&.ant-typography`,
 * `forms/FormTicketSingle/Summary/components/wrappers.tsx` and
 * `ui/TypographyNoOverflowPrint.tsx` both select the ellipsis trio
 * (`.ant-typography-ellipsis`, `-single-line`, `-ellipsis-single-line`), and
 * four test files query `.ant-typography`, `-secondary`, `-success` and
 * `-danger` from the DOM.
 *
 * [development] THE COLOURS ARE INLINE STYLES, NOT UTILITIES, and that is the
 * one substantive divergence from the reference. antd is deleted over there,
 * so its typography stylesheet can never be injected and a
 * `text-muted-foreground` utility always wins. Here antd is still installed:
 * if anything on the page renders an antd component that pulls the typography
 * styles in, `.ant-typography{color:…}` and `.ant-typography.ant-typography-
 * secondary{color:…}` arrive UNLAYERED — and unlayered CSS outranks everything
 * in Tailwind's `@layer utilities` regardless of specificity. A utility would
 * silently lose and `type="secondary"` would render as body text. An inline
 * style outranks both, so the colours below are applied that way and read from
 * the same tokens the utility would have (`--muted-foreground`, `--destructive`
 * from `src/styles/tailwind.css`; `--wh-green-600` / `--wh-gold-*` from
 * `src/styles/ThemeVars`). `!important` would also have won, but it would beat
 * a CALL SITE's own `style={{ color }}` too, and six live tags pass one.
 *
 * KNOWN DIVERGENCE, from the same root cause as the one below that IS closed:
 * this seam clamps with CSS, whereas antd's own clamp is JS-measured too —
 *   (i)  `ellipsis.expandable` ALWAYS renders its expand trigger, even when
 *        the text fits. Two call sites (event-log-kit DeletionEvent, the
 *        history deleted tab). The reference branch behaves the same way.
 * (ii) used to read "the tooltip is always off in shadcn mode" — CLOSED. The
 * `tooltip` config is now backed by a real `scrollWidth`/`scrollHeight` vs.
 * `clientWidth`/`clientHeight` measurement (`useOverflowMeasurement` below),
 * observed with a `ResizeObserver` so a later resize (a column drag, a
 * viewport change) re-evaluates it. The Tooltip mounts ONLY when the
 * measurement says the text is actually clipped, on the SAME element the CSS
 * clamp is applied to (the hoisted inner span when there is an expand
 * trigger to keep outside the clamp, the root node otherwise) — never on
 * text that fits. The measurement itself runs ONLY when a `tooltip` was
 * asked for at all (the default from `src/storybook/components/Typography`
 * now matches antd's `{ tooltip: children }` again, see its `getEllipsisProp`);
 * a bare `ellipsis={true}`/`ellipsis={{ rows: 2 }}` with no `tooltip` costs
 * nothing beyond the CSS clamp, exactly as before. That is what keeps the
 * ~430 call sites cheap: a mounted `ResizeObserver` per tooltip-carrying
 * instance, never a mounted Tooltip/Radix trigger for text that fits.
 * ------------------------------------------------------------------------- */

type TypographyType = 'secondary' | 'success' | 'warning' | 'danger'

type EllipsisConfig = {
  rows?: number
  expandable?: boolean
  symbol?: React.ReactNode
  tooltip?: React.ReactNode
  onExpand?: (event: React.MouseEvent<HTMLElement>) => void
}

type CopyableConfig = {
  text?: string
  icon?: React.ReactNode
  onCopy?: () => void
}

type EditableConfig = {
  icon?: React.ReactNode
  tooltip?: React.ReactNode
  maxLength?: number
  onStart?: () => void
  onChange?: (value: string) => void
  onCancel?: () => void
  onEnd?: () => void
}

type BaseProps = {
  type?: TypographyType
  disabled?: boolean
  strong?: boolean
  italic?: boolean
  underline?: boolean
  delete?: boolean
  code?: boolean
  mark?: boolean
  keyboard?: boolean
  ellipsis?: boolean | EllipsisConfig
  copyable?: boolean | CopyableConfig
  children?: React.ReactNode
}

/**
 * antd's semantic colours, as the class name live stylesheets and tests select
 * plus the colour value itself. See the `[development]` note above for why the
 * value is not a utility.
 */
const TYPE_STYLE: Record<TypographyType, { className: string; color: string }> =
  {
    secondary: {
      className: 'ant-typography-secondary',
      color: 'var(--muted-foreground)',
    },
    success: {
      className: 'ant-typography-success',
      color: 'var(--wh-green-600)',
    },
    warning: {
      className: 'ant-typography-warning',
      color: 'var(--wh-gold-600)',
    },
    danger: {
      className: 'ant-typography-danger',
      color: 'var(--destructive)',
    },
  }

/** antd wraps the child in real elements for these, rather than styling the
 *  root — `<strong>` etc. carry the semantics assistive tech reads. */
const decorate = (node: React.ReactNode, props: BaseProps): React.ReactNode => {
  let out = node
  if (props.code)
    out = (
      <code className="rounded border border-border bg-muted px-1 py-0.5 text-[0.9em]">
        {out}
      </code>
    )
  if (props.keyboard)
    out = (
      <kbd className="rounded border border-border bg-muted px-1 py-0.5 text-[0.9em]">
        {out}
      </kbd>
    )
  if (props.mark)
    out = <mark className="bg-[var(--wh-gold-200)] px-0.5">{out}</mark>
  if (props.delete) out = <del>{out}</del>
  if (props.underline) out = <u>{out}</u>
  if (props.italic) out = <i>{out}</i>
  if (props.strong) out = <strong>{out}</strong>
  return out
}

const toEllipsisConfig = (
  ellipsis: BaseProps['ellipsis']
): EllipsisConfig | null => {
  if (!ellipsis) return null
  return typeof ellipsis === 'object' ? ellipsis : {}
}

const toCopyableConfig = (
  copyable: BaseProps['copyable']
): CopyableConfig | null => {
  if (!copyable) return null
  return typeof copyable === 'object' ? copyable : {}
}

const toEditableConfig = (editable: $TSFixMe): EditableConfig | null => {
  if (!editable) return null
  return typeof editable === 'object' ? (editable as EditableConfig) : {}
}

/** The plain-text a node can contribute to copy/edit affordances. A React
 *  element has none — `String(node)` would hand the user "[object Object]". */
const plainText = (node: React.ReactNode): string | null => {
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (Array.isArray(node)) {
    const parts = node.map(plainText)
    return parts.every((part) => part !== null) ? parts.join('') : null
  }
  return null
}

/** The copy affordance antd renders after the content.
 *
 *  antd takes 'Copy'/'Copied'/'Edit'/'Expand' from its locale pack; this seam
 *  routes them through Transifex Native `t()` instead. All four SOURCES are
 *  already registered on Transifex — other call sites push the identical
 *  literal (`t('Edit')` in compliance-documents.tsx, `t('Copy')` in
 *  CopyTransferEventModal.tsx, `t('Copied')` in the standalone-contacts
 *  cockpit, `t('Expand')` in WorkspaceRail.tsx), and Transifex Native keys a
 *  source by `md5('5:'+source+':'+context)` regardless of which file calls
 *  `t()`, so this seam resolves the SAME registered key. Verified against the
 *  Transifex API (`GET /resource_strings/o:wastehero:p:wastehero_frontend-1:
 *  r:wastehero_frontend-1:s:<md5(key+':')>`): all four return 200. */
const CopyButton = ({
  config,
  content,
}: {
  config: CopyableConfig
  content: React.ReactNode
}) => {
  const [copied, setCopied] = React.useState(false)
  const timer = React.useRef<ReturnType<typeof setTimeout>>()

  React.useEffect(() => () => clearTimeout(timer.current), [])

  const copy = async () => {
    const text = config.text ?? plainText(content)
    // Nothing sensible to copy (a React-element child with no `copyable.text`),
    // or no Clipboard API at all (a non-secure context has none): report
    // nothing rather than flipping to "Copied" having copied nothing.
    if (text === null || !navigator.clipboard) return
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // A denied clipboard permission must not take the page down; antd is
      // equally silent here.
      return
    }
    config.onCopy?.()
    setCopied(true)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setCopied(false), 3000)
  }

  return (
    /* Flush: these are inline text affordances, not chrome. The `[data-slot]`
       base layer resets Chrome's UA `button { padding: 1px 6px }`, which this
       never wanted — antd renders the same affordance as an `<a>` with no
       padding, and `ml-1` is the whole gap it needs. */
    <button
      type="button"
      data-slot="typography-copy"
      aria-label={copied ? t('Copied') : t('Copy')}
      className="ant-typography-copy ml-1 inline-flex cursor-pointer align-baseline text-muted-foreground hover:text-foreground"
      onClick={copy}
    >
      {copied ? <CheckOutlined /> : config.icon ?? <CopyOutlined />}
    </button>
  )
}

/** The pencil that opens the inline editor. Its accessible name is the
 *  configured `tooltip` when that is plain text, so a screen reader gets the
 *  same wording a sighted user hovers for. */
const EditTrigger = ({
  config,
  onStart,
}: {
  config: EditableConfig
  onStart: () => void
}) => {
  const label = plainText(config.tooltip) ?? t('Edit')
  const button = (
    /* Flush, as `CopyTrigger` above. */
    <button
      type="button"
      data-slot="typography-edit"
      aria-label={label}
      className="ant-typography-edit ml-1 inline-flex cursor-pointer align-baseline text-muted-foreground hover:text-foreground"
      onClick={() => {
        config.onStart?.()
        onStart()
      }}
    >
      {config.icon ?? <EditOutlined />}
    </button>
  )
  return config.tooltip === undefined || config.tooltip === null ? (
    button
  ) : (
    <Tooltip title={config.tooltip}>{button}</Tooltip>
  )
}

/** antd's inline-edit affordance: a pencil that swaps the text for an input.
 *  Enter or blur commits through `onChange`; Escape cancels without it. */
const EditInput = ({
  initialValue,
  config,
  onDone,
}: {
  initialValue: string
  config: EditableConfig
  onDone: () => void
}) => {
  const [value, setValue] = React.useState(initialValue)
  const cancelled = React.useRef(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const commit = () => {
    if (cancelled.current) return
    config.onChange?.(value)
    config.onEnd?.()
    onDone()
  }

  const cancel = () => {
    cancelled.current = true
    config.onCancel?.()
    config.onEnd?.()
    onDone()
  }

  return (
    <input
      ref={inputRef}
      data-slot="typography-edit-input"
      className="ant-typography-edit-content w-full appearance-none border border-border bg-transparent px-1 [font-family:inherit] [font-size:inherit] [line-height:inherit]"
      value={value}
      maxLength={config.maxLength}
      onChange={(event) => setValue(event.target.value)}
      onBlur={commit}
      onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          commit()
        } else if (event.key === 'Escape') {
          event.preventDefault()
          cancel()
        }
      }}
    />
  )
}

type RenderProps = BaseProps & {
  as: 'span' | 'div' | 'a' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5'
  slot: string
  className?: string
  style?: React.CSSProperties
  [key: string]: $TSFixMe
}

/** Whether the clamped node is genuinely clipping its content right now —
 *  the antd-parity check for `ellipsis.tooltip`. Runs ONLY when `active` (a
 *  `tooltip` was actually configured); a clamp with no tooltip request never
 *  pays for the observer. Single-row clamps compare `scrollWidth` against
 *  `clientWidth`, multi-row clamps compare `scrollHeight` against
 *  `clientHeight` — both are false (0 vs 0) until layout has actually run, so
 *  a `ResizeObserver` is what turns them true once the browser has measured
 *  the real box. A test environment with no layout engine (jsdom) reports no
 *  overflow unless a test stubs the two properties — see
 *  `ellipsis-tooltip-dom.test.tsx`. */
const useOverflowMeasurement = (
  active: boolean,
  singleLine: boolean,
  text: string | null
) => {
  // The measured node is STATE, not a ref: when the verdict flips, `Typed`
  // swaps its root between the bare `Component` and a Tooltip-wrapped one, so
  // React unmounts the old DOM node and mounts a fresh one. A ref would leave
  // the `ResizeObserver` attached to the detached element and no later resize
  // would ever re-measure. Holding the node in state puts it in the effect's
  // dependency array, so the observer re-attaches to whatever node is live.
  const [node, setNode] = React.useState<HTMLElement | null>(null)
  const [isOverflowing, setIsOverflowing] = React.useState(false)

  React.useLayoutEffect(() => {
    if (!active || !node) {
      setIsOverflowing(false)
      return undefined
    }
    const measure = () =>
      setIsOverflowing(
        singleLine
          ? node.scrollWidth > node.clientWidth
          : node.scrollHeight > node.clientHeight
      )
    // Re-measures on every `text` change too: a same-size box whose TEXT
    // changed fires no `ResizeObserver` entry (the border box did not
    // resize), so a length change on an unchanged column width would
    // otherwise keep a stale verdict. The key is the PLAIN TEXT rather than
    // the raw `children`, because a JSX child is a new identity on every
    // parent render and would tear down and rebuild the observer each time;
    // non-plain-text children key on `null` and rely on the observer's own
    // box-size detection.
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [active, node, singleLine, text])

  return { setNode, isOverflowing }
}

const Typed = React.forwardRef<HTMLElement, RenderProps>((props, ref) => {
  const {
    as,
    slot,
    type,
    disabled,
    strong,
    italic,
    underline,
    delete: del,
    code,
    mark,
    keyboard,
    ellipsis,
    copyable,
    className,
    style,
    children,
    editable,
    // antd-only props that must never reach the DOM
    prefixCls: _prefixCls,
    ...rest
  } = props

  const ellipsisConfig = toEllipsisConfig(ellipsis)
  const copyableConfig = toCopyableConfig(copyable)
  const editableConfig = toEditableConfig(editable)
  const rows = ellipsisConfig?.rows ?? 1
  const singleLine = Boolean(ellipsisConfig) && rows === 1

  const [expanded, setExpanded] = React.useState(false)
  const [editing, setEditing] = React.useState(false)
  const clamped = Boolean(ellipsisConfig) && !expanded

  const childText = plainText(children)
  const editableText = editableConfig ? childText : null
  const canEdit = editableConfig !== null && editableText !== null

  const content = decorate(children, {
    strong,
    italic,
    underline,
    delete: del,
    code,
    mark,
    keyboard,
  })

  const Component = as as React.ElementType

  const expandTrigger =
    ellipsisConfig?.expandable && clamped ? (
      /* Flush, as `CopyTrigger` above. */
      <button
        type="button"
        data-slot="typography-expand"
        className="ant-typography-expand ml-1 cursor-pointer text-primary"
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          setExpanded(true)
          ellipsisConfig.onExpand?.(event)
        }}
      >
        {ellipsisConfig.symbol ?? t('Expand')}
      </button>
    ) : null

  // A multi-row clamp (`display:-webkit-box; -webkit-line-clamp; overflow
  // hidden`) clips its own children, so the control that RELEASES the clamp
  // cannot live inside it — it would be laid out after the text and hidden in
  // exactly the case that needs it. When there is a trigger, the clamp moves
  // onto an inner element and the trigger becomes its SIBLING.
  const multiRowClamp = clamped && !singleLine
  const hoistClamp = multiRowClamp && expandTrigger !== null

  // `ellipsis={{ tooltip }}` is the single most common form in this repo (the
  // storybook wrapper passes `{ tooltip: children }` by default), so truncated
  // text stays readable on hover. `tooltip: false` opts out. The measurement
  // only runs when a tooltip was actually requested (see
  // `useOverflowMeasurement`'s doc comment) — an `ellipsis` with no `tooltip`
  // pays nothing beyond the CSS clamp.
  const tooltip = ellipsisConfig?.tooltip
  const tooltipRequested =
    tooltip !== undefined && tooltip !== false && tooltip !== null
  const measuring = clamped && tooltipRequested
  const { setNode: setOverflowNode, isOverflowing } = useOverflowMeasurement(
    measuring,
    singleLine,
    childText
  )

  // The measurement ref targets whichever element actually carries the CSS
  // clamp: the hoisted inner span when there is an expand trigger to keep
  // outside it, the root node otherwise — never the outer node when it has an
  // expand trigger as a SIBLING, or `scrollWidth` would include the trigger.
  const setClampRef = React.useCallback(
    (node: HTMLElement | null) => {
      if (measuring) setOverflowNode(node)
    },
    [measuring, setOverflowNode]
  )

  const setRootRef = React.useCallback(
    (node: HTMLElement | null) => {
      if (measuring && !hoistClamp) setOverflowNode(node)
      if (typeof ref === 'function') ref(node)
      // eslint-disable-next-line no-param-reassign
      else if (ref) ref.current = node
    },
    [ref, hoistClamp, measuring, setOverflowNode]
  )

  const clampStyle = {
    // Multi-row clamping has no utility equivalent that takes a dynamic row
    // count, so it is inline.
    display: '-webkit-box',
    WebkitLineClamp: rows,
    WebkitBoxOrient: 'vertical' as const,
  }

  // antd 5.15.4 `typography/style/mixins.js:212-218` scopes an extra rule to the
  // `a` and `span` roots only — Link and Text:
  //   `a&-ellipsis, span&-ellipsis { display: inline-block; max-width: 100% }`
  // An inline box ignores `overflow: hidden`, so without it `truncate` has NO
  // effect on those roots and the text overflows instead of clipping. Paragraph
  // is a `div` (already block-level) and antd deliberately left it out.
  const inlineClampStyle =
    clamped && singleLine && (as === 'span' || as === 'a')
      ? { display: 'inline-block', maxWidth: '100%' }
      : null

  const body = hoistClamp ? (
    <span
      ref={setClampRef as React.Ref<HTMLSpanElement>}
      data-slot="typography-ellipsis-content"
      className="overflow-hidden"
      style={clampStyle}
    >
      {content}
    </span>
  ) : (
    content
  )

  const node = (
    <Component
      {...rest}
      ref={setRootRef}
      data-slot={slot}
      className={cn(
        'ant-typography',
        type && TYPE_STYLE[type].className,
        disabled && 'ant-typography-disabled cursor-not-allowed opacity-50',
        // The three names two live stylesheets select on.
        clamped && 'ant-typography-ellipsis',
        clamped && singleLine && 'ant-typography-single-line',
        clamped && singleLine && 'ant-typography-ellipsis-single-line',
        // `truncate` is antd's white-space/overflow/text-overflow trio; the
        // inline-block/max-width half of the rule is applied as inline style
        // below (see `inlineClampStyle`).
        clamped && singleLine && 'truncate',
        multiRowClamp && !hoistClamp && 'overflow-hidden',
        className
      )}
      style={{
        // The colour goes FIRST so a call site's own `style={{ color }}` still
        // wins; see the `[development]` note in the header for why it is here
        // rather than in a utility class.
        ...(type ? { color: TYPE_STYLE[type].color } : null),
        ...(multiRowClamp && !hoistClamp ? clampStyle : null),
        ...inlineClampStyle,
        ...style,
      }}
    >
      {editing && canEdit ? (
        <EditInput
          initialValue={editableText as string}
          config={editableConfig as EditableConfig}
          onDone={() => setEditing(false)}
        />
      ) : (
        <>
          {body}
          {expandTrigger}
          {canEdit ? (
            <EditTrigger
              config={editableConfig as EditableConfig}
              onStart={() => setEditing(true)}
            />
          ) : null}
          {copyableConfig ? (
            <CopyButton config={copyableConfig} content={children} />
          ) : null}
        </>
      )}
    </Component>
  )

  // Mount the Tooltip only once the measurement confirms the text is
  // genuinely clipped — the antd-parity check `useOverflowMeasurement` runs
  // above.
  if (tooltipRequested && isOverflowing) {
    return (
      <Tooltip title={tooltip === true ? children : tooltip}>{node}</Tooltip>
    )
  }

  return node
})

Typed.displayName = 'Typography.Base'

/* ------------------------------------------------------------- the exports */

export type TextProps = AntdTextProps
export type ParagraphProps = AntdParagraphProps
export type LinkProps = AntdLinkProps
export type TitleProps = AntdTitleProps

const Text = React.forwardRef<HTMLElement, TextProps>((props, ref) => (
  <Typed {...props} ref={ref} as="span" slot="typography-text" />
))
Text.displayName = 'Typography.Text'

const Paragraph = React.forwardRef<HTMLElement, ParagraphProps>(
  (props, ref) => (
    <Typed
      {...props}
      ref={ref}
      as="div"
      slot="typography-paragraph"
      className={cn('mb-4 last:mb-0', props.className)}
    />
  )
)
Paragraph.displayName = 'Typography.Paragraph'

const Link = React.forwardRef<HTMLElement, LinkProps>((props, ref) => (
  <Typed
    {...props}
    ref={ref}
    as="a"
    slot="typography-link"
    className={cn(
      'ant-typography-link cursor-pointer text-primary hover:underline',
      props.className
    )}
  />
))
Link.displayName = 'Typography.Link'

/** antd's Title levels, resolved against the app's type scale rather than
 *  antd's own font sizes. */
const TITLE_VARIANT = {
  1: 'header-32',
  2: 'header-24',
  3: 'subHeader-20',
  4: 'subHeader-16',
  5: 'body-14',
} as const

const Title = React.forwardRef<HTMLElement, TitleProps>((props, ref) => {
  const { level = 1, style, className, ...rest } = props
  const tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5'
  return (
    <Typed
      {...rest}
      ref={ref}
      as={tag}
      slot="typography-title"
      className={cn('mb-2 font-semibold last:mb-0', className)}
      style={{ ...variants[TITLE_VARIANT[level]], ...style }}
    />
  )
})
Title.displayName = 'Typography.Title'

/**
 * antd's bare `<Typography>` is a plain wrapper element, NOT a Text — it has
 * no `copyable`/`ellipsis` behaviour of its own and silently ignores them.
 * Reproduced as-is (the props are swallowed rather than passed to the DOM) so
 * the handful of bare call sites keep rendering exactly what they render
 * today; treating it as a Text here would be a behaviour change, not a fix.
 */
type TypographyRootProps = {
  children?: React.ReactNode
  className?: string
  [key: string]: $TSFixMe
}

const TypographyRoot = React.forwardRef<HTMLDivElement, TypographyRootProps>(
  (props, ref) => {
    const {
      className,
      children,
      prefixCls: _prefixCls,
      copyable: _copyable,
      ellipsis: _ellipsis,
      ...rest
    } = props
    return (
      <article
        {...rest}
        ref={ref}
        data-slot="typography"
        className={cn('ant-typography', className)}
      >
        {children}
      </article>
    )
  }
)
TypographyRoot.displayName = 'Typography'

export const Typography = Object.assign(TypographyRoot, {
  Text,
  Title,
  Paragraph,
  Link,
})

export { Text, Title, Paragraph, Link }
